import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { SubscriptionPortalFlow } from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { LIGHT_GREY } from "styles/oneOffColor";

import { useCheckout } from "components/StripeCheckout/useCheckout";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import CancelQuestion from "./Question";
import {
  CancelScreen,
  ProCancelQuestions,
  QuestionsToAsk,
  VaCancelQuestions,
} from "./schema";
import styles from "./styles";

let LOSS_ITEMS = [
  {
    icon: "fa-regular fa-address-book",
    text: (
      <>
        <strong>400,000+ exclusive brand contacts</strong> available to paid
        subscribers only
      </>
    ),
  },
  {
    icon: "fa-regular fa-paper-plane",
    text: (
      <>
        <strong>Campaign pitching</strong> and <strong>Local Pitching</strong>{" "}
        opportunities
      </>
    ),
  },
  {
    icon: "fa-regular fa-envelope-open-text",
    text: (
      <>
        <strong>Email tracking</strong> — you won't know when brands open your
        emails
      </>
    ),
  },
];

interface Props {
  open: boolean;
  closeDialog: () => void;
}

const CancelFlow = ({ open, closeDialog }: Props) => {
  const { getToken } = useAuth();
  const { plan, subscription, plans } = useContext(SubscriptionContext);
  const { handleManagePlan } = useSubscription();
  const { currentOrg, organizationProProfile } = useContext(
    OrganizationUserContext,
  );
  const { setErrorAlert } = useContext(AlertContext);
  const { getCurrentEmailPackages } = useCheckout();

  const DialogTitles = {
    [CancelScreen.question]:
      currentOrg?.id && currentOrg?.id % 2 === 0 ? (
        <>Before you go, a few questions...</>
      ) : (
        <>Are you sure you want to cancel? </>
      ),
    [CancelScreen.gifted_credits]: <>One last gift for you! </>,
    [CancelScreen.gifted_credits_granted]: <>Credits applied! 🎉</>,
  };

  const [screen, setScreen] = useState<CancelScreen>(CancelScreen.question);
  const [hasGiftedCredits, setHasGiftedCredits] = useState(false);
  const [transactionsInCents, setTransactionsInCents] = useState(0);
  const [mainLoading, setMainLoading] = useState(false);
  const [questionsToAsk, setQuestionsToAsk] = useState<QuestionsToAsk[]>([]);

  const activeMonth = Number(subscription?.subscription?.totalMonthsSince || 0);
  const successMonth = Math.max(activeMonth + 1, 2);

  const totalEmails = plans?.[Plans.PRO]?.monthly_limit || 0;
  const grandfatheredLimit = organizationProProfile?.planConfig?.monthlyLimit;
  const hasGrandfatheredEmails =
    grandfatheredLimit != null && grandfatheredLimit > totalEmails;
  const extraEmails = hasGrandfatheredEmails
    ? grandfatheredLimit! - totalEmails
    : 0;

  const handleChange = (question: string, answer: string) => {
    setQuestionsToAsk((prev) =>
      prev.map((questionToAsk) =>
        questionToAsk.question === question
          ? { ...questionToAsk, answer }
          : questionToAsk,
      ),
    );
  };

  const hasEmailVolumes = getCurrentEmailPackages() > 0;
  const emailItems = {
    icon: "fa-regular fa-paper-plane",
    text: (
      <>
        <strong>{50 * getCurrentEmailPackages()} bonus emails </strong> from
        your email volume add-on
      </>
    ),
  };

  const resetState = () => {
    setScreen(CancelScreen.question);
    closeDialog();
  };

  const verifiedIfUserHasReceivedCredits = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/paid`,
        "GET",
      );
      if (res.paid) {
        setHasGiftedCredits(true);
      }
    } catch (error) {
      // Do nothing
    }
  };

  const trackAnswer = async (isPrimaryButtonClicked: boolean) => {
    const buttonText = isPrimaryButtonClicked
      ? primaryTexts[screen]
      : secondaryTexts[screen];

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/track`,
        "POST",
        {},
        { buttonText, screen },
      );
    } catch (error) {
      // Do nothing
    }
  };

  const grantCredits = async () => {
    setMainLoading(true);
    try {
      const { grantedCreditsInCents } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/grant-credits`,
        "POST",
      );
      if (!grantedCreditsInCents || grantedCreditsInCents <= 0) {
        resetState();
        return;
      }
      setTransactionsInCents(grantedCreditsInCents);
      setHasGiftedCredits(true);
      setScreen(CancelScreen.gifted_credits_granted);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setMainLoading(false);
    }
  };

  const nextPrimaryStep = {
    [CancelScreen.question]: resetState,
    [CancelScreen.gifted_credits]: grantCredits,
    [CancelScreen.gifted_credits_granted]: resetState,
  };

  const nextSecondaryStep = {
    [CancelScreen.question]: () => {
      if (!hasGiftedCredits) {
        setScreen(CancelScreen.gifted_credits);
      } else {
        handleManagePlan(plan!, SubscriptionPortalFlow.subscription_cancel);
      }
    },
    [CancelScreen.gifted_credits]: () => {
      handleManagePlan(plan!, SubscriptionPortalFlow.subscription_cancel);
    },
    [CancelScreen.gifted_credits_granted]: () => {
      handleManagePlan(plan!);
    },
  };

  const primaryTexts = {
    [CancelScreen.question]: "No, keep my access",
    [CancelScreen.gifted_credits]: "Accept This Offer",
    [CancelScreen.gifted_credits_granted]: "Close",
  };

  const secondaryTexts = {
    [CancelScreen.question]: "Yes, continue to cancel",
    [CancelScreen.gifted_credits]: "Cancel Subscription Anyway",
    [CancelScreen.gifted_credits_granted]: "View Next Invoice",
  };

  const primaryText = primaryTexts[screen];
  const secondaryText = secondaryTexts[screen];

  useEffect(() => {
    if (currentOrg?.id) {
      verifiedIfUserHasReceivedCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    const currentOrgId = currentOrg?.id || 0;
    if (currentOrg?.isProUser && currentOrgId % 2 === 0) {
      trackEvent("Cancel Flow - Questions Shown");
      setQuestionsToAsk(ProCancelQuestions);
    } else if (currentOrg?.isVaUser && currentOrgId % 2 !== 0) {
      trackEvent("Cancel Flow - Questions Shown");
      setQuestionsToAsk(VaCancelQuestions);
    } else {
      trackEvent("Cancel Flow - Warning Lost Access Shown");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.isProUser]);

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={resetState}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
          backgroundColor: LIGHT_GREY,
        },
      }}
    >
      <IconButton sx={styles.closeButton} onClick={resetState}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>

      <DialogContent sx={{ mt: 3, px: { xs: 2, md: 6 } }}>
        {/* Warning icon */}
        <Box sx={{ textAlign: "center", mb: 2, fontSize: 30 }}>⚠️</Box>

        <Typography variant="h4" textAlign="center" sx={{ mb: 3 }}>
          {DialogTitles[screen]}
        </Typography>

        {screen === CancelScreen.question &&
          questionsToAsk.length > 0 &&
          questionsToAsk?.map((question, index) => (
            <CancelQuestion
              description={question.question}
              answer={question.answer}
              handleChange={handleChange}
              key={`cancel-question-${index}`}
            />
          ))}

        {screen === CancelScreen.question && questionsToAsk?.length === 0 && (
          <>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1.5 }}>
              By cancelling, you'll lose access to:
            </Typography>
            <List disablePadding sx={{ mb: 2 }}>
              {hasEmailVolumes && (
                <ListItem
                  alignItems="flex-start"
                  disableGutters
                  sx={styles.boldItemStyling}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      component="i"
                      className={emailItems.icon}
                      sx={{ fontSize: 14, color: "#DC2626" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={emailItems.text}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              )}
              {LOSS_ITEMS.map((item, i) => (
                <ListItem
                  key={i}
                  disableGutters
                  alignItems="flex-start"
                  sx={styles.itemStyling}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      component="i"
                      className={item.icon}
                      sx={{ fontSize: 14, color: "#DC2626" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              ))}
              {extraEmails > 0 && (
                <ListItem
                  disableGutters
                  alignItems="flex-start"
                  sx={styles.boldItemStyling}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      component="i"
                      className={"fa-regular fa-envelope-open-text"}
                      sx={{ fontSize: 16, color: "#B91C1C" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        You will lose access to{" "}
                        <strong>
                          {extraEmails.toLocaleString()} extra emails/month
                        </strong>{" "}
                        included in your plan if you cancel. This is a
                        grandfathered benefit that is not available to new
                        subscribers.
                      </>
                    }
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              )}
            </List>

            {/* Downgrade note */}
            <Typography
              sx={{
                fontSize: 13,
                color: "black",
                textAlign: "center",
                mb: 3,
              }}
            >
              You'll be downgraded to the{" "}
              <strong>
                Free Plan ({plans && plans[Plans.FREE]?.monthly_limit} emails /
                month){" "}
              </strong>{" "}
              at the end of your current billing period.
            </Typography>
          </>
        )}

        {screen === CancelScreen.gifted_credits && (
          <>
            <Typography textAlign="center" sx={{ mb: 3, fontSize: 16 }}>
              Creators who stay active for{" "}
              <strong>{successMonth} months</strong> see much higher success
              than those who leave{" "}
              {activeMonth <= 0
                ? "within the first month"
                : `after ${activeMonth} month${activeMonth > 1 ? "s" : ""}`}
              .
            </Typography>
            <Typography sx={{ fontSize: 80 }} textAlign="center">
              🎁
            </Typography>
            <Typography textAlign="center" sx={{ fontSize: 16 }}>
              Want 50% off your next month to stay active?
            </Typography>
          </>
        )}

        {screen === CancelScreen.gifted_credits_granted && (
          <Typography textAlign="center" sx={{ mb: 3, fontSize: 16 }}>
            You've received $
            <Box component="span" sx={{ fontSize: 30, color: "purple.main" }}>
              {Number(transactionsInCents / 100).toFixed(2)}
            </Box>{" "}
            credits for your next month! 💰
            <br />
            <br />
            It has been auto-applied to your next billing cycle. To view your
            next invoice, you can click on the button below.
          </Typography>
        )}

        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 3, mb: 1 }}
          flexDirection="column"
          gap={1}
        >
          {primaryText && (
            <LoadingButton
              sx={styles.primaryButton}
              variant="contained"
              disableElevation
              onClick={() => {
                trackAnswer(true);
                nextPrimaryStep[screen]();
              }}
              loading={mainLoading}
            >
              {primaryText}
            </LoadingButton>
          )}
          {secondaryText && secondaryText.length > 0 && (
            <Button
              sx={styles.secondaryButton}
              onClick={() => {
                trackAnswer(false);
                nextSecondaryStep[screen]();
              }}
            >
              {secondaryText}
            </Button>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CancelFlow;
